// [FICHIER] Service vérification accès interviews - Support switch automatique compagnie
// [RÔLE] Validation accès cross-company + détection besoin switch + feedback UX
// [ARCHITECTURE] API calls + cache + error handling + types TypeScript

import { FamlaServices } from './index';

// [INTERFACES] Types pour réponses API et gestion accès
export interface InterviewAccessResponse {
    has_access: boolean;
    current_company: boolean;
    target_company_id: string | null;
    collaboration: any | null;
}

export interface AccessCheckResult {
    success: boolean;
    needsSwitch: boolean;
    targetCompanyId: string | null;
    error?: string;
    data?: InterviewAccessResponse;
}

// [CACHE] Cache simple pour éviter appels API répétés
const accessCache = new Map<string, { result: AccessCheckResult; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class InterviewAccessService {
    
    // [MÉTHODE PRINCIPALE] Vérification accès interview avec cache
    static async checkInterviewAccess(
        processId: string, 
        accessToken: string
    ): Promise<AccessCheckResult> {
        
        // [VALIDATION PARAMS] Vérification paramètres requis
        if (!processId || !accessToken) {
            return {
                success: false,
                needsSwitch: false,
                targetCompanyId: null,
                error: 'Missing required parameters'
            };
        }

        // [CACHE CHECK] Vérifier cache avant appel API
        const cacheKey = `${processId}-${accessToken.substring(0, 10)}`;
        const cached = accessCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            console.log('🎯 [InterviewAccessService] Using cached result for process:', processId);
            return cached.result;
        }

        try {
            console.log('🔍 [InterviewAccessService] Checking access for process:', processId);
            
            // [API CALL] Appel endpoint de vérification d'accès
            const response = await FamlaServices(
                "api_host", 
                `/interview/access-check/${processId}`, 
                "GET", 
                "", 
                accessToken
            );

            // [TRAITEMENT RÉPONSE] Analyse réponse API
            if (response.status === 200) {
                const data: InterviewAccessResponse = response.body.data;
                
                const result: AccessCheckResult = {
                    success: true,
                    needsSwitch: !data.current_company && data.has_access,
                    targetCompanyId: data.target_company_id,
                    data: data
                };

                // [CACHE RESULT] Stocker en cache
                accessCache.set(cacheKey, { result, timestamp: Date.now() });
                
                console.log('✅ [InterviewAccessService] Access check result:', {
                    hasAccess: data.has_access,
                    currentCompany: data.current_company,
                    needsSwitch: result.needsSwitch,
                    targetCompanyId: data.target_company_id
                });

                return result;
            }
            
            // [CAS ERREUR] Gestion erreurs HTTP spécifiques
            else if (response.status === 403) {
                return {
                    success: true,
                    needsSwitch: false,
                    targetCompanyId: null,
                    error: 'Access denied to this process'
                };
            }
            else if (response.status === 404) {
                return {
                    success: false,
                    needsSwitch: false,
                    targetCompanyId: null,
                    error: 'Process not found'
                };
            }
            else {
                return {
                    success: false,
                    needsSwitch: false,
                    targetCompanyId: null,
                    error: `API error: ${response.status}`
                };
            }

        } catch (error) {
            console.error('❌ [InterviewAccessService] Error checking access:', error);
            return {
                success: false,
                needsSwitch: false,
                targetCompanyId: null,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    // [MÉTHODE UTILITAIRE] Nettoyage cache (pour tests ou refresh)
    static clearCache(): void {
        accessCache.clear();
        console.log('🧹 [InterviewAccessService] Cache cleared');
    }

    // [MÉTHODE UTILITAIRE] Nettoyage cache expiré
    static cleanExpiredCache(): void {
        const now = Date.now();
        for (const [key, value] of accessCache.entries()) {
            if (now - value.timestamp > CACHE_DURATION) {
                accessCache.delete(key);
            }
        }
    }
}

export default InterviewAccessService;
